import { useEffect, useRef } from 'preact/hooks'

// The full preview's frame takes focus like any control; a thumbnail's frame opts out with tabindex -1.
const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe:not([tabindex="-1"]), [tabindex="0"]'

/**
 * The keyboard contract of a full-screen template dialog: focus moves in when it opens and back to whatever opened it
 * when it closes, Tab wraps inside it, and Escape closes it alone. The builder behind the chooser closes itself on
 * Escape from a document listener, so this one listens in the capture phase and stops the key there, as
 * VariableCatalogPanel does. Put `initialFocusRef` on the control that should take focus first; else the dialog does.
 */
export function useDialogFocus<T extends HTMLElement>(onEscape: () => void) {
    const dialogRef = useRef<HTMLDivElement>(null)
    const initialFocusRef = useRef<T>(null)
    // Read through a ref so a re-render with a new handler does not re-register the listener.
    const onEscapeRef = useRef(onEscape)
    onEscapeRef.current = onEscape

    useEffect(() => {
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const initial = initialFocusRef.current ?? dialogRef.current
        initial?.focus()

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation()
                event.preventDefault()
                onEscapeRef.current()
                return
            }
            const dialog = dialogRef.current
            if (event.key !== 'Tab' || !dialog) return
            const elements = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)]
            const first = elements[0]
            const last = elements[elements.length - 1]
            const active = document.activeElement
            if (!first || !last) {
                event.preventDefault()
                return
            }
            if (!dialog.contains(active)) {
                event.preventDefault()
                first.focus()
            } else if (event.shiftKey && (active === first || active === dialog)) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && active === last) {
                event.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('keydown', onKey, true)
            opener?.focus()
        }
    }, [])

    return { dialogRef, initialFocusRef }
}
